import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CalendarEvent } from '../types';
import { toast } from 'sonner';

// Action types for undo/redo
type ActionType = 'create' | 'update' | 'delete' | 'bulk_delete';

interface UndoAction {
  type: ActionType;
  timestamp: number;
  // For create: store the created shift data
  createdShift?: CalendarEvent;
  // For update: store the old data
  oldShift?: CalendarEvent;
  newShift?: CalendarEvent;
  // For delete: store the deleted shift data
  deletedShift?: CalendarEvent;
  // For bulk delete: store all deleted shifts
  deletedShifts?: CalendarEvent[];
}

const MAX_HISTORY_SIZE = 20;

export const useUndoRedo = (refetch: () => void, teamId?: string | null) => {
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoAction[]>([]);
  const [isUndoing, setIsUndoing] = useState(false);

  // Clear history when team changes
  useEffect(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, [teamId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          undo();
        }
      }
      // Check for Ctrl+Shift+Z or Cmd+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        if (canRedo) {
          redo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undoStack, redoStack]);

  // Add action to undo stack
  const addAction = useCallback((action: UndoAction) => {
    setUndoStack(prev => {
      const newStack = [...prev, action];
      // Limit stack size
      if (newStack.length > MAX_HISTORY_SIZE) {
        return newStack.slice(newStack.length - MAX_HISTORY_SIZE);
      }
      return newStack;
    });
    // Clear redo stack when new action is added
    setRedoStack([]);
  }, []);

  // Undo last action
  const undo = useCallback(async () => {
    if (undoStack.length === 0 || isUndoing) return;

    setIsUndoing(true);
    const action = undoStack[undoStack.length - 1];

    try {
      switch (action.type) {
        case 'create':
          // Undo create by deleting the shift
          if (action.createdShift) {
            const { error } = await supabase
              .from('team_schedules')
              .delete()
              .eq('id', action.createdShift.id);

            if (error) throw error;
            toast.success('Shift creation undone');
          }
          break;

        case 'update':
          // Undo update by restoring old data
          if (action.oldShift) {
            const { guard_names, ...oldData } = action.oldShift;
            const { error } = await supabase
              .from('team_schedules')
              .update(oldData)
              .eq('id', action.oldShift.id);

            if (error) throw error;
            toast.success('Shift update undone');
          }
          break;

        case 'delete':
          // Undo delete by recreating the shift
          if (action.deletedShift) {
            const { guard_names, ...shiftData } = action.deletedShift;
            const { error } = await supabase
              .from('team_schedules')
              .insert(shiftData);

            if (error) throw error;
            toast.success('Shift deletion undone');
          }
          break;

        case 'bulk_delete':
          // Undo bulk delete by recreating all shifts
          if (action.deletedShifts && action.deletedShifts.length > 0) {
            const shiftsToInsert = action.deletedShifts.map(shift => {
              const { guard_names, ...shiftData } = shift;
              return shiftData;
            });

            const { error } = await supabase
              .from('team_schedules')
              .insert(shiftsToInsert);

            if (error) throw error;
            toast.success(`${action.deletedShifts.length} shifts restored`);
          }
          break;
      }

      // Move action from undo to redo stack
      setUndoStack(prev => prev.slice(0, -1));
      setRedoStack(prev => [...prev, action]);

      // Refresh the calendar
      refetch();
    } catch (error) {
      console.error('Error undoing action:', error);
      toast.error('Failed to undo action');
    } finally {
      setIsUndoing(false);
    }
  }, [undoStack, isUndoing, refetch]);

  // Redo last undone action
  const redo = useCallback(async () => {
    if (redoStack.length === 0 || isUndoing) return;

    setIsUndoing(true);
    const action = redoStack[redoStack.length - 1];

    try {
      switch (action.type) {
        case 'create':
          // Redo create by inserting the shift again
          if (action.createdShift) {
            const { guard_names, ...shiftData } = action.createdShift;
            const { error } = await supabase
              .from('team_schedules')
              .insert(shiftData);

            if (error) throw error;
            toast.success('Shift creation redone');
          }
          break;

        case 'update':
          // Redo update by applying new data
          if (action.newShift) {
            const { guard_names, ...newData } = action.newShift;
            const { error } = await supabase
              .from('team_schedules')
              .update(newData)
              .eq('id', action.newShift.id);

            if (error) throw error;
            toast.success('Shift update redone');
          }
          break;

        case 'delete':
          // Redo delete by deleting the shift again
          if (action.deletedShift) {
            const { error } = await supabase
              .from('team_schedules')
              .delete()
              .eq('id', action.deletedShift.id);

            if (error) throw error;
            toast.success('Shift deletion redone');
          }
          break;

        case 'bulk_delete':
          // Redo bulk delete by deleting all shifts again
          if (action.deletedShifts && action.deletedShifts.length > 0) {
            const shiftIds = action.deletedShifts.map(shift => shift.id);
            const { error } = await supabase
              .from('team_schedules')
              .delete()
              .in('id', shiftIds);

            if (error) throw error;
            toast.success(`${action.deletedShifts.length} shifts deleted`);
          }
          break;
      }

      // Move action from redo to undo stack
      setRedoStack(prev => prev.slice(0, -1));
      setUndoStack(prev => [...prev, action]);

      // Refresh the calendar
      refetch();
    } catch (error) {
      console.error('Error redoing action:', error);
      toast.error('Failed to redo action');
    } finally {
      setIsUndoing(false);
    }
  }, [redoStack, isUndoing, refetch]);

  // Clear all history
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  const canUndo = undoStack.length > 0 && !isUndoing;
  const canRedo = redoStack.length > 0 && !isUndoing;

  return {
    addAction,
    undo,
    redo,
    clearHistory,
    canUndo,
    canRedo,
    undoStack,
    redoStack
  };
};
